import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProfilePage.css';
import {
  getUserInfo,
  getFollowers,
  getFollowing,
  updateUserProfile,
  searchUsers,
  followUser,
  unfollowUser,
} from '../services/api';

function ProfilePage() {
  const userId = Number(localStorage.getItem('userId'));
  const [user, setUser] = useState({});
  const [bio, setBio] = useState('');
  const [profilePic, setProfilePic] = useState('');
  const [followersCount, setFollowersCount] = useState(0);
  const [followingIds, setFollowingIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const fileInputRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalUsers, setModalUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);

  const USERS_PER_PAGE = 8;
  const navigate = useNavigate();

  const openUserListModal = async (type) => {
    setModalTitle(type === 'followers' ? 'Followers' : 'Following');
    setShowModal(true);
    setModalLoading(true);
    try {
      const idList = type === 'followers'
        ? await getFollowers(userId)
        : await getFollowing(userId);

        const enrichedUsers = await Promise.all(
          idList.map(async ({ id }) => {
            try {
              const user = await getUserInfo(id);
              return { ...user, id }; // <-- ensure ID is preserved
            } catch {
              return { id, username: 'Unknown', profile_pic: '' };
            }
          })
        );
        
      setModalUsers(enrichedUsers);
    } catch (err) {
      console.error(`Failed to fetch ${type}:`, err);
      setModalUsers([]);
    }
    finally {
      setModalLoading(false); // Stop loading
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalUsers([]);
  };

  const fetchProfileData = async () => {
    setLoading(true);
    try {
      const [userData, followersList, followingList] = await Promise.all([
        getUserInfo(userId),
        getFollowers(userId),
        getFollowing(userId),
      ]);

      setUser(userData);
      setBio(userData.bio || '');
      setProfilePic(userData.profile_pic || '');
      setFollowersCount(followersList.length);
      setFollowingIds(followingList.map((u) => u.id));
    } catch (err) {
      console.error('Failed to fetch profile data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) fetchProfileData();
  }, [userId]);

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage('');
    try {
      await updateUserProfile(userId, bio, profilePic);
      setSaveMessage('Profile updated!');
    } catch (err) {
      setSaveMessage('Failed to update profile.');
      console.error(err);
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePic(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSearch = async () => {
    if (searchQuery.trim().length < 3) {
      setSearchResults([]);
      return;
    }

    try {
      const results = await searchUsers(searchQuery.trim());
      const filtered = results.filter((u) => u.id !== userId);
      setSearchResults(filtered);
      setCurrentPage(1);

      const updatedFollowing = await getFollowing(userId);
      setFollowingIds(updatedFollowing.map((u) => u.id));
    } catch (err) {
      console.error('Search failed:', err);
    }
  };

  const handleFollowToggle = async (targetId) => {
    try {
      const isFollowing = followingIds.includes(targetId);

      if (isFollowing) {
        await unfollowUser(userId, targetId);
        setFollowingIds((prev) => prev.filter((id) => id !== targetId));
      } else {
        await followUser(userId, targetId);
        setFollowingIds((prev) => [...prev, targetId]);
      }
    } catch (err) {
      console.error('Follow/unfollow error:', err);
    }
  };

  const paginatedResults = searchResults.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );

  const totalPages = Math.ceil(searchResults.length / USERS_PER_PAGE);

  const isFollowingUser = (id) => followingIds.includes(id);
  
  // Skeleton loading UI
  if (loading) {
    return (
      <div className="profile-page skeleton">
        <div className="profile-container">
          <div className="profile-left">
            <div className="profile-picture skeleton-element">
              <div className="shimmer-wrapper">
                <div className="shimmer"></div>
              </div>
            </div>
          </div>

          <div className="profile-right">
            <div className="skeleton-username skeleton-element">
              <div className="shimmer-wrapper">
                <div className="shimmer"></div>
              </div>
            </div>

            <div className="profile-meta">
              <div className="skeleton-meta-button skeleton-element">
                <div className="shimmer-wrapper">
                  <div className="shimmer"></div>
                </div>
              </div>
              <div className="skeleton-meta-button skeleton-element">
                <div className="shimmer-wrapper">
                  <div className="shimmer"></div>
                </div>
              </div>
            </div>

            <div className="skeleton-description skeleton-element">
              <div className="shimmer-wrapper">
                <div className="shimmer"></div>
              </div>
            </div>

            <div className="skeleton-button skeleton-element">
              <div className="shimmer-wrapper">
                <div className="shimmer"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal UI
  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-left">
          <div className="profile-picture" onClick={() => fileInputRef.current.click()}>
            <img
              src={profilePic || '/resources/blank-profile.webp'}
              alt="Profile"
              className="profile-img"
            />
            <div className="edit-overlay">Edit</div>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleImageChange}
            />
          </div>
        </div>

        <div className="profile-right">
          <h2 className="profile-username">@{user.username || '...'}</h2>

          <div className="profile-meta">
            <button className="meta-button" onClick={() => openUserListModal('following')}>
              {followingIds.length} Following
            </button>
            <button className="meta-button" onClick={() => openUserListModal('followers')}>
              {followersCount} Followers
            </button>
          </div>

          <div className="profile-description">
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Write something about yourself..."
              rows={4}
            />
          </div>

          <button
            onClick={handleSave}
            className="save-profile-button"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>

          {saveMessage && <div className="save-message">{saveMessage}</div>}

        </div>
      </div>

      {showModal && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{modalTitle}</h3>
            <button className="close-modal" onClick={closeModal}>✖</button>
            {modalLoading ? (
              <div className="modal-shimmer-loader" />
            ) : modalUsers.length > 0 ? (
              <ul className="modal-user-list">
                {modalUsers.map((u, index) => (
                  <li key={u.id || `user-${index}`} className="modal-user-item">
                    <img
                      src={u.profile_pic || '/resources/blank-profile.webp'}
                      alt={u.username || 'Unknown'}
                      className="user-avatar"
                    />
                    <span className="username">@{u.username || 'unknown'}</span>
                    <button
                      className="view-profile-btn"
                      onClick={() => navigate(`/profile/${u.id}`)}
                    >
                     View Profile
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="empty-message">No {modalTitle.toLowerCase()}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfilePage;